import { useMsal } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { useCallback } from 'react'
import { apiRequest, graphApiRequest, getCustomerGraphRequest, customerGraphMsalConfig } from '../config/authConfig'

// Create a separate MSAL instance for Customer Graph app
// This uses a different app registration that only has Microsoft Graph permissions
let customerGraphMsalInstance: PublicClientApplication | null = null

const getCustomerGraphMsalInstance = async (): Promise<PublicClientApplication> => {
  if (!customerGraphMsalInstance) {
    customerGraphMsalInstance = new PublicClientApplication(customerGraphMsalConfig)
    await customerGraphMsalInstance.initialize()
  }
  return customerGraphMsalInstance
}

export function useAuth() {
  const { instance, accounts } = useMsal()
  const account = accounts[0]

  const getAccessToken = useCallback(async () => {
    const currentAccount = instance.getAllAccounts()[0]
    if (!currentAccount) {
      throw new Error('No active account')
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: currentAccount,
      })
      return response.accessToken
    } catch (error) {
      // If silent token acquisition fails, try interactive
      const response = await instance.acquireTokenPopup(apiRequest)
      return response.accessToken
    }
  }, [instance])

  // Get access token for Microsoft Graph API (used for M365 user management)
  // This token supports MFA via interactive login when required
  const getGraphAccessToken = useCallback(async () => {
    const currentAccount = instance.getAllAccounts()[0]
    if (!currentAccount) {
      throw new Error('No active account')
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...graphApiRequest,
        account: currentAccount,
      })
      return response.accessToken
    } catch (error) {
      // If silent token acquisition fails, try interactive (this handles MFA)
      const response = await instance.acquireTokenPopup(graphApiRequest)
      return response.accessToken
    }
  }, [instance])

  // Get access token for a specific customer tenant's Microsoft Graph API
  // This uses GDAP - user's access is determined by their AdminAgents/HelpdeskAgents membership
  // If user doesn't have GDAP access to the customer, authentication will fail
  //
  // IMPORTANT: This uses a SEPARATE Azure AD app (Customer Graph app) that only has
  // Microsoft Graph permissions. This avoids consent issues caused by the Partner Center
  // API scope which doesn't exist in customer tenants.
  //
  // Options:
  // - silentOnly: if true, won't open popup on failure (use to check if auth is needed)
  // - forceConsent: if true, forces the consent dialog to create service principal
  const getCustomerGraphAccessToken = useCallback(async (
    customerTenantId: string,
    options?: { silentOnly?: boolean; forceConsent?: boolean }
  ) => {
    // Use the separate Customer Graph MSAL instance
    const customerMsal = await getCustomerGraphMsalInstance()

    const request = getCustomerGraphRequest(customerTenantId)
    const { silentOnly = false, forceConsent = false } = options || {}

    // Get login hint from main MSAL instance (the user's email)
    // This helps the popup know which user to authenticate
    const mainAccount = instance.getAllAccounts()[0]
    const loginHint = mainAccount?.username

    // Check if we have any accounts in the customer graph instance
    const accounts = customerMsal.getAllAccounts()
    const currentAccount = accounts[0]

    // If forceConsent, skip silent and go directly to popup with consent prompt
    if (forceConsent) {
      try {
        const response = await customerMsal.acquireTokenPopup({
          ...request,
          prompt: 'consent', // Force consent dialog to create service principal
          loginHint, // Use login hint to pre-fill the email
        })
        return response.accessToken
      } catch (popupError) {
        const errorMsg = popupError instanceof Error ? popupError.message : String(popupError)
        if (errorMsg.includes('AADSTS90099') || errorMsg.includes('has not been authorized')) {
          throw new Error('AADSTS90099: App has not been authorized in this customer tenant. Click "Grant Consent" to enable access.')
        }
        if (errorMsg.includes('AADSTS50020') || errorMsg.includes('User account does not exist')) {
          throw new Error('You do not have GDAP access to this customer. Contact your admin to be added to AdminAgents or HelpdeskAgents security group.')
        }
        throw popupError
      }
    }

    // If we have an account, try silent first
    if (currentAccount) {
      try {
        // Try silent token acquisition first
        const response = await customerMsal.acquireTokenSilent({
          ...request,
          account: currentAccount,
        })
        return response.accessToken
      } catch (error) {
        // Check for AADSTS90099 in the silent error (app not authorized)
        const silentErrorMsg = error instanceof Error ? error.message : String(error)
        console.log('[CustomerGraph] Silent acquisition failed:', silentErrorMsg)

        if (silentErrorMsg.includes('AADSTS90099') || silentErrorMsg.includes('has not been authorized')) {
          throw new Error('AADSTS90099: App has not been authorized in this customer tenant. Click "Grant Consent" to enable access.')
        }

        // If silentOnly mode, don't try popup - just throw the error
        if (silentOnly) {
          throw new Error('NEEDS_AUTHORIZATION: Silent token acquisition failed. User needs to authorize the app first.')
        }

        // Silent failed - fall through to popup for any recoverable error
        // This includes: interaction_required, monitor_window_timeout, no_tokens_found, etc.
        console.log('[CustomerGraph] Silent failed, will try popup')
      }
    }

    // If silentOnly and no account or silent failed, throw error
    if (silentOnly) {
      console.log('[CustomerGraph] silentOnly mode, throwing NEEDS_AUTHORIZATION')
      throw new Error('NEEDS_AUTHORIZATION: Silent token acquisition failed. User needs to authorize the app first.')
    }

    // Use interactive popup (either no account or silent failed)
    // Include loginHint to help identify the user
    try {
      console.log('[CustomerGraph] Attempting token popup for tenant:', customerTenantId)
      console.log('[CustomerGraph] loginHint:', loginHint)
      console.log('[CustomerGraph] request scopes:', request.scopes)
      console.log('[CustomerGraph] request authority:', request.authority)
      const response = await customerMsal.acquireTokenPopup({
        ...request,
        loginHint,
      })
      console.log('[CustomerGraph] Popup succeeded, got token')
      return response.accessToken
    } catch (popupError) {
      console.error('[CustomerGraph] Popup error:', popupError)
      console.error('[CustomerGraph] Popup error type:', popupError?.constructor?.name)
      // Check for specific GDAP-related errors
      const errorMsg = popupError instanceof Error ? popupError.message : String(popupError)
      if (errorMsg.includes('AADSTS90099') || errorMsg.includes('has not been authorized')) {
        throw new Error('AADSTS90099: App has not been authorized in this customer tenant. Click "Grant Consent" to enable access.')
      }
      if (errorMsg.includes('AADSTS50020') || errorMsg.includes('User account does not exist')) {
        throw new Error('You do not have GDAP access to this customer. Contact your admin to be added to AdminAgents or HelpdeskAgents security group.')
      }
      if (errorMsg.includes('AADSTS65001')) {
        throw new Error('Admin consent is required for this customer tenant. Please contact your admin.')
      }
      throw popupError
    }
  }, [instance])

  return {
    getAccessToken,
    getGraphAccessToken,
    getCustomerGraphAccessToken,
    user: account,
    isAuthenticated: accounts.length > 0,
  }
}
